import React, { useEffect, useMemo, useState } from "react";
import {
  addDays, addMonths, endOfMonth, endOfWeek, format, isSameDay, isSameMonth,
  parseISO, startOfMonth, startOfWeek, subMonths,
} from "date-fns";
import { api } from "../lib/api";
import { openTopLevel } from "../lib/openTopLevel";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, X, AlertCircle } from "lucide-react";

const VIEWS = ["month", "week", "day", "agenda"];

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const [gcalConfigured, setGcalConfigured] = useState(false);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const tmin = startOfMonth(cursor).toISOString();
    const tmax = endOfMonth(cursor).toISOString();
    const { data } = await api.get("/calendar/events", { params: { time_min: tmin, time_max: tmax } });
    setEvents(data.events || []);
    setConnected(data.connected);
  };

  const loadStatus = async () => {
    const { data } = await api.get("/google/calendar/status");
    setGcalConfigured(data.configured);
  };

  useEffect(() => { load(); }, [cursor]);
  useEffect(() => {
    loadStatus();
    // handle ?gcal=connected
    const params = new URLSearchParams(window.location.search);
    if (params.get("gcal") === "connected") toast.success("Google Calendar connected");
    if (params.get("gcal") === "error") {
      const reason = params.get("reason") || "unknown";
      toast.error(`Google Calendar connection failed (${reason})`);
    }
  }, []);

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/google/calendar/connect");
      openTopLevel(data.authorization_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Google Calendar OAuth");
    }
  };

  const onDay = (day) => {
    setCursor(day);
    setCreating(true);
  };

  const eventsForDay = (day) => events.filter((e) => {
    const s = e.start?.dateTime || e.start?.date || e.start;
    if (!s) return false;
    try { return isSameDay(typeof s === "string" ? parseISO(s) : new Date(s), day); } catch { return false; }
  });

  return (
    <div data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Schedule</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          {!connected && (
            <button data-testid="calendar-connect-gcal" onClick={connectGoogle} className="rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">{gcalConfigured ? "Connect Google Calendar" : "Configure Google Calendar"}</button>
          )}
          <button data-testid="calendar-new-event" onClick={() => setCreating(true)} className="inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] active:scale-[0.98]"><Plus size={16} /> New Event</button>
        </div>
      </div>

      {!connected && !gcalConfigured && (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold">Google Calendar not yet configured</div>
            <p className="mt-1 leading-relaxed">Events you create here are stored locally until you connect Google Calendar. Add your Google OAuth credentials in <strong>Settings → Google Calendar</strong> to enable real two-way sync.</p>
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <div className="flex items-center gap-1 rounded-md border border-zinc-200 bg-white p-1">
          {VIEWS.map((v) => (
            <button key={v} data-testid={`calendar-view-${v}`} onClick={() => setView(v)} className={`rounded px-3 py-1 text-xs font-medium capitalize ${view === v ? "bg-[#E5F0FF] text-[#0066ff]" : "text-zinc-600 hover:bg-zinc-50"}`}>{v}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setCursor(view === "month" ? subMonths(cursor, 1) : addDays(cursor, view === "week" ? -7 : -1))} className="rounded-md border border-zinc-200 p-1.5 hover:bg-zinc-50"><ChevronLeft size={14} /></button>
          <button onClick={() => setCursor(new Date())} className="rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium hover:bg-zinc-50">Today</button>
          <button onClick={() => setCursor(view === "month" ? addMonths(cursor, 1) : addDays(cursor, view === "week" ? 7 : 1))} className="rounded-md border border-zinc-200 p-1.5 hover:bg-zinc-50"><ChevronRight size={14} /></button>
        </div>
        <div className="text-sm font-medium text-zinc-800">{format(cursor, view === "month" ? "MMMM yyyy" : "MMM d, yyyy")}</div>
        {connected && <span className="ml-auto rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">Google Calendar synced</span>}
      </div>

      <div className="mt-4">
        {view === "month" && <MonthView cursor={cursor} eventsForDay={eventsForDay} onDay={onDay} onEvent={setEditing} />}
        {view === "week" && <WeekView cursor={cursor} eventsForDay={eventsForDay} onDay={onDay} onEvent={setEditing} />}
        {view === "day" && <DayView cursor={cursor} eventsForDay={eventsForDay} onEvent={setEditing} />}
        {view === "agenda" && <AgendaView events={events} onEvent={setEditing} />}
      </div>

      {(creating || editing) && (
        <EventModal
          event={editing}
          defaultDate={cursor}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function MonthView({ cursor, eventsForDay, onDay, onEvent }) {
  const start = startOfWeek(startOfMonth(cursor));
  const end = endOfWeek(endOfMonth(cursor));
  const days = [];
  let d = start;
  while (d <= end) { days.push(d); d = addDays(d, 1); }

  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white">
      <div className="grid grid-cols-7 border-b border-zinc-200 text-[11px] font-semibold uppercase tracking-widest text-zinc-500">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (<div key={d} className="px-3 py-2 text-center">{d}</div>))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const inMonth = isSameMonth(day, cursor);
          const today = isSameDay(day, new Date());
          const dayEvents = eventsForDay(day);
          return (
            <button
              key={i}
              data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
              onClick={() => onDay(day)}
              className={`min-h-[110px] border-b border-r border-zinc-100 p-2 text-left transition-colors hover:bg-zinc-50 ${!inMonth ? "bg-zinc-50/50" : ""}`}
            >
              <div className={`mb-1 inline-flex h-6 min-w-[24px] items-center justify-center rounded-full text-xs font-medium ${today ? "bg-[#0066ff] text-white" : inMonth ? "text-zinc-800" : "text-zinc-400"}`}>{format(day, "d")}</div>
              <div className="space-y-1">
                {dayEvents.slice(0, 3).map((e, idx) => {
                  const title = e.summary || e.title || "(no title)";
                  return (
                    <div key={idx} onClick={(ev) => { ev.stopPropagation(); onEvent(e); }} className="truncate rounded bg-[#E5F0FF] px-1.5 py-0.5 text-[11px] font-medium text-[#0066ff] hover:bg-[#0066ff] hover:text-white">{title}</div>
                  );
                })}
                {dayEvents.length > 3 && <div className="text-[10px] text-zinc-500">+{dayEvents.length - 3} more</div>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, eventsForDay, onDay, onEvent }) {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }).map((_, i) => addDays(start, i));
  return (
    <div className="grid grid-cols-7 gap-2">
      {days.map((d) => {
        const evs = eventsForDay(d);
        return (
          <button key={d.toISOString()} onClick={() => onDay(d)} className="flex min-h-[200px] flex-col rounded-xl border border-zinc-200 bg-white p-3 text-left hover:bg-zinc-50">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{format(d, "EEE")}</div>
            <div className="text-lg font-semibold text-zinc-900">{format(d, "d")}</div>
            <div className="mt-2 flex flex-col gap-1">
              {evs.map((e, i) => (<div key={i} onClick={(ev) => { ev.stopPropagation(); onEvent(e); }} className="truncate rounded bg-[#E5F0FF] px-2 py-1 text-[11px] font-medium text-[#0066ff]">{e.summary || e.title}</div>))}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function DayView({ cursor, eventsForDay, onEvent }) {
  const evs = eventsForDay(cursor);
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-6">
      <div className="text-sm font-semibold text-zinc-800">{format(cursor, "EEEE, MMM d")}</div>
      <div className="mt-4 flex flex-col gap-2">
        {evs.length ? evs.map((e, i) => {
          const s = e.start?.dateTime || e.start?.date || e.start;
          return (<button key={i} onClick={() => onEvent(e)} className="rounded-lg border border-zinc-100 px-4 py-3 text-left hover:bg-zinc-50"><div className="text-sm font-medium text-zinc-900">{e.summary || e.title}</div><div className="text-xs text-zinc-500">{s && (typeof s === "string" ? format(parseISO(s), "p") : "")}</div></button>);
        }) : <div className="text-sm text-zinc-500">No events today.</div>}
      </div>
    </div>
  );
}

function AgendaView({ events, onEvent }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-6">
      <div className="flex flex-col gap-2">
        {events.length ? events.map((e, i) => {
          const s = e.start?.dateTime || e.start?.date || e.start;
          return (<button key={i} onClick={() => onEvent(e)} className="flex items-center justify-between rounded-lg border border-zinc-100 px-4 py-3 text-left hover:bg-zinc-50"><div><div className="text-sm font-medium text-zinc-900">{e.summary || e.title}</div><div className="text-xs text-zinc-500">{e.description}</div></div><div className="text-xs text-zinc-500">{s && (typeof s === "string" ? format(parseISO(s), "MMM d, p") : "")}</div></button>);
        }) : <div className="text-sm text-zinc-500">No events in range.</div>}
      </div>
    </div>
  );
}

function EventModal({ event, defaultDate, onClose, onSaved }) {
  const isEdit = Boolean(event?.id);
  const initStart = (event?.start?.dateTime || event?.start?.date || defaultDate?.toISOString() || new Date().toISOString()).slice(0, 16);
  const initEnd = (event?.end?.dateTime || event?.end?.date || addDays(defaultDate || new Date(), 0).toISOString()).slice(0, 16);
  const [title, setTitle] = useState(event?.summary || event?.title || "");
  const [description, setDescription] = useState(event?.description || "");
  const [start, setStart] = useState(initStart);
  const [end, setEnd] = useState(initEnd);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    const payload = { title, description, start: new Date(start).toISOString(), end: new Date(end).toISOString(), all_day: false };
    try {
      if (isEdit && event.id) {
        await api.put(`/calendar/events/${event.id}`, payload);
      } else {
        await api.post("/calendar/events", payload);
      }
      toast.success("Event saved");
      onSaved();
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const remove = async () => {
    if (!event?.id) return;
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/calendar/events/${event.id}`);
      toast.success("Deleted");
      onSaved();
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="event-modal" className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-semibold tracking-tight text-zinc-900">{isEdit ? "Edit Event" : "New Event"}</h2>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>
        <div className="mt-6 flex flex-col gap-3">
          <label className="text-sm"><span className="text-xs font-medium text-zinc-600">Title</span><input data-testid="event-form-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20" /></label>
          <label className="text-sm"><span className="text-xs font-medium text-zinc-600">Description</span><textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-sm"><span className="text-xs font-medium text-zinc-600">Start</span><input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm" /></label>
            <label className="text-sm"><span className="text-xs font-medium text-zinc-600">End</span><input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1.5 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm" /></label>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-between gap-2">
          <div>{isEdit && event?.id && <button onClick={remove} className="text-sm font-medium text-red-600 hover:underline">Delete</button>}</div>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
            <button data-testid="event-modal-save" onClick={save} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] active:scale-[0.98]">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
